import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

/**
 * 비전문가용 첫 사용 가이드(#18).
 * - 콘솔 상단: 접이식 "처음이세요?" 3스텝 빠른 사용법
 * - 콘솔 하단: 접이식 용어집(F0·MIDI·cents·MPM·YIN·dBFS·p95·voiced)
 * 펼침/닫힘 상태는 localStorage로 기억한다.
 */

const STORAGE_PREFIX = 'muzly:debug:';

/** localStorage로 펼침 상태를 기억하는 boolean 상태. SSR/차단 환경에서도 안전. */
const useRememberedOpen = (key: string, fallback: boolean) => {
  const storageKey = `${STORAGE_PREFIX}${key}`;
  const [open, setOpen] = useState(fallback);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(storageKey);
      if (saved !== null) {
        setOpen(saved === '1');
      }
    } catch {
      // 접근 불가(프라이빗 모드 등)면 fallback 유지.
    }
  }, [storageKey]);

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(storageKey, next ? '1' : '0');
      } catch {
        // 저장 실패는 무시 — 이번 세션 동안만 반영.
      }
      return next;
    });
  };

  return [open, toggle] as const;
};

interface CollapsibleCardProps {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}

const CollapsibleCard = ({
  title,
  open,
  onToggle,
  children,
}: CollapsibleCardProps) => (
  <section className="rounded-2xl border border-border/40 bg-background">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center gap-2 px-4 py-3 text-left"
    >
      <span className="flex-1 font-display text-sm font-semibold tracking-tight">
        {title}
      </span>
      <span className="text-xs text-muted-foreground">{open ? '▾' : '▸'}</span>
    </button>
    {open && (
      <div className="border-t border-border/30 px-4 py-4 text-sm text-muted-foreground">
        {children}
      </div>
    )}
  </section>
);

const STEPS: { label: string; text: string }[] = [
  {
    label: '① 시작',
    text: '세션 제어 패널에서 "시작"을 누릅니다. 마이크 권한을 허용하세요.',
  },
  {
    label: '② 노래/소리',
    text: '마이크에 대고 노래하거나 소리를 냅니다. "아—" 하고 길게 내면 잘 잡힙니다.',
  },
  {
    label: '③ 확인',
    text: '그래프에 음높이 곡선이, 리드아웃에 음이름이 뜨면 정상입니다. 잡음이 섞이면 config의 silence threshold를 올려보세요.',
  },
];

/** 콘솔 상단에 두는 접이식 첫 사용 가이드. */
export const FirstTimeGuide = () => {
  const [open, toggle] = useRememberedOpen('guide-open', true);

  return (
    <CollapsibleCard
      title="처음이세요? 빠른 사용법"
      open={open}
      onToggle={toggle}
    >
      <ol className="space-y-2">
        {STEPS.map((step) => (
          <li key={step.label} className="flex gap-2">
            <span className="shrink-0 font-semibold text-foreground">
              {step.label}
            </span>
            <span className="leading-relaxed">{step.text}</span>
          </li>
        ))}
      </ol>
    </CollapsibleCard>
  );
};

const TERMS: { term: string; def: string }[] = [
  { term: 'F0', def: '기본 주파수(Hz) — 소리의 음높이. 높을수록 고음.' },
  {
    term: 'MIDI',
    def: '음높이를 0~127 정수로 표현한 번호. 60 = 가운데 도(C4).',
  },
  {
    term: 'cents',
    def: '반음을 100등분한 미세 음정 단위. 음정이 얼마나 정확한지 표현.',
  },
  {
    term: 'MPM',
    def: 'McLeod Pitch Method — 기본 음높이 추정 알고리즘(빠름).',
  },
  { term: 'YIN', def: '또 다른 음높이 추정 알고리즘. MPM과 비교 검증용.' },
  { term: 'dBFS', def: '디지털 음량 단위. 0이 최대, 작을수록 조용함(음수).' },
  {
    term: 'p95',
    def: '상위 95퍼센타일 — 가장 느린 5%를 제외한 최악값. 성능 지표.',
  },
  {
    term: 'voiced',
    def: '소리(유성음)가 실제로 있는지 여부. false면 무음/잡음.',
  },
];

/** 콘솔 하단에 두는 접이식 용어집. */
export const Glossary = () => {
  const [open, toggle] = useRememberedOpen('glossary-open', false);

  return (
    <CollapsibleCard title="용어집" open={open} onToggle={toggle}>
      <dl className="space-y-2">
        {TERMS.map((entry) => (
          <div key={entry.term} className="flex gap-2">
            <dt className="w-16 shrink-0 font-mono text-xs font-semibold text-foreground">
              {entry.term}
            </dt>
            <dd className="flex-1 leading-relaxed">{entry.def}</dd>
          </div>
        ))}
      </dl>
    </CollapsibleCard>
  );
};
